import {CircularProgress, Box, Button, Stack, Typography} from '@mui/material';
import {Navigate, Outlet, useLocation, useNavigate} from 'react-router-dom';
import {AuthContext} from '../../models/AuthContext';
import {ApiErrorFeedback} from '../common/ApiErrorFeedback';
import {useAppTranslation} from '@mybake/ui/hooks/useAppTranslation';

type GuardProps = {
    auth?: AuthContext;
    isLoading: boolean;
    error?: unknown;
    retry: () => void;
    children?: React.ReactNode;
};

const requestedPath = (pathname: string, search: string, hash: string) => `${pathname}${search}${hash}`;

const GuardContent = ({children}: {children?: React.ReactNode}) => <>{children ?? <Outlet/>}</>;

const AuthLoading = () => (
    <Box sx={{minHeight: '70vh', display: 'grid', placeItems: 'center'}}>
        <CircularProgress/>
    </Box>
);

export const RequireAuthenticated = ({auth, isLoading, error, retry, children}: GuardProps) => {
    const location = useLocation();

    if (isLoading) return <AuthLoading/>;
    if (error) return <Box sx={{p: 3}}><ApiErrorFeedback error={error} retryAction={<Button onClick={retry}>Retry</Button>}/></Box>;
    if (!auth?.isAuthenticated) {
        return <Navigate replace to="/login" state={{from: requestedPath(location.pathname, location.search, location.hash)}}/>;
    }

    return <GuardContent>{children}</GuardContent>;
};

type PermissionGuardProps = GuardProps & {
    permission: string;
};

export const RequirePermission = ({auth, isLoading, error, retry, permission, children}: PermissionGuardProps) => {
    const location = useLocation();

    if (isLoading) return <AuthLoading/>;
    if (error) return <Box sx={{p: 3}}><ApiErrorFeedback error={error} retryAction={<Button onClick={retry}>Retry</Button>}/></Box>;
    if (!auth?.isAuthenticated) {
        return <Navigate replace to="/login" state={{from: requestedPath(location.pathname, location.search, location.hash)}}/>;
    }
    if (!auth.permissions.includes(permission)) return <AccessDenied/>;

    return <GuardContent>{children}</GuardContent>;
};

export const AccessDenied = () => {
    const {t} = useAppTranslation();
    const navigate = useNavigate();

    return (
        <Stack alignItems="center" justifyContent="center" spacing={2} sx={{minHeight: '70vh', textAlign: 'center', p: 3}}>
            <Typography component="h1" variant="h3">{t('auth:access_denied_title')}</Typography>
            <Typography color="text.secondary">{t('auth:access_denied_body')}</Typography>
            <Button variant="contained" onClick={() => navigate('/app')}>{t('auth:return_to_app')}</Button>
        </Stack>
    );
};

export const isSafeAuthenticatedReturnPath = (value: unknown): value is string =>
    typeof value === 'string' && (
        value === '/app' || value.startsWith('/app/') ||
        value === '/admin' || value.startsWith('/admin/')
    );
